// Top bar (progress + pills + action buttons) shared between
// PickingScreen's main pick layout and the simplified hero used for
// starting-location / first-tote / drop awaiting-tote phases. The
// simplified hero used to render no chrome — the picker had no way to
// pause or back out of the page while it was up.
import { Pause, AlertTriangle, ArrowLeft, Route } from "lucide-react";
import { useTranslation } from "../../i18n";
import "./PickingScreen.css";

export type PickingTopBarProps = {
    priority?: "low" | "medium" | "high" | null;
    itemsTotal: number;
    itemsPicked: number;
    /** Pick-type label strip between the progress line and the pill row —
     *  "Single", "Pick & Sort", "Putwall", "Large" — so a picker can tell at
     *  a glance which flow they are in. Its own row rather than a pill: the
     *  pill row is already full on a 360px handheld, and another pill there
     *  would squeeze the tote id to an ellipsis. Omitted → no strip. */
    pickTypeLabel?: string;
    /** Tints the strip per flow (see .ps-type-strip--* in PickingScreen.css). */
    pickTypeKind?: string;
    /** Slot for module-specific badges (tier badge, open-tote pill, etc.). */
    barExtras?: React.ReactNode;
    /** Opens the "Units left by aisle" popup. Omitted → no button. */
    onAisles?: () => void;
    onPause?: () => void;
    onAlert?: () => void;
    onBack?: () => void;
    pauseDisabled?: boolean;
    alertDisabled?: boolean;
    backDisabled?: boolean;
    /** Show the 10px progress bar above the top bar. Defaults to true. */
    showProgress?: boolean;
};

const PickingTopBar = (props: PickingTopBarProps) => {
    const { t } = useTranslation();

    const progressPct = props.itemsTotal > 0
        ? (props.itemsPicked / props.itemsTotal) * 100
        : 0;

    const prioClass =
        props.priority === "high"   ? "ps-pill--prio-hi"  :
        props.priority === "medium" ? "ps-pill--prio-med" :
                                      "ps-pill--prio-low";

    const showProgress = props.showProgress !== false;

    return (
        <>
            {showProgress && (
                <div className="ps-progress" role="progressbar"
                     aria-valuenow={Math.round(progressPct)}
                     aria-valuemin={0} aria-valuemax={100}>
                    <div className="ps-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
            )}

            {props.pickTypeLabel && (
                <div
                    className={"ps-type-strip" + (props.pickTypeKind ? " ps-type-strip--" + props.pickTypeKind : "")}
                    role="status"
                    aria-label={t("picking_pick_type_strip_label", { type: props.pickTypeLabel })}
                >
                    {props.pickTypeLabel}
                </div>
            )}

            {/* Two groups: the info pills (left) and the action buttons
                (right). One row, everything vertically centred — the
                info group never wraps; if space runs out the tote pill
                shrinks with an ellipsis instead of pushing the meta
                count (or the buttons) onto a second line. Order: item
                count, priority, module extras (tier badge / tote). */}
            <div className="ps-bar">
                <div className="ps-bar-info">
                    <span className="ps-pill ps-pill--meta">
                        <span className="ps-meta-count">{props.itemsPicked}/{props.itemsTotal}</span>
                        <span className="ps-meta-unit">{t("picking_items_unit") || "items"}</span>
                    </span>
                    {props.priority && (
                        <span className={"ps-pill " + prioClass}>
                            {props.priority === "high"   ? t("priority_high") :
                             props.priority === "medium" ? t("priority_medium") :
                                                           t("priority_low")}
                        </span>
                    )}
                    {props.barExtras}
                </div>
                <div className="ps-bar-actions">
                    {props.onAisles && (
                        <button type="button" className="ps-icon-btn"
                            onClick={props.onAisles}
                            aria-label={t("picking_aisle_units_btn")}
                            title={t("picking_aisle_units_btn")}>
                            <Route size={20} />
                        </button>
                    )}
                    {props.onPause && (
                        <button type="button" className="ps-icon-btn"
                            onClick={props.onPause}
                            disabled={props.pauseDisabled}
                            aria-label={t("picking_pause_btn")}
                            title={t("picking_pause_btn")}>
                            <Pause size={20} />
                        </button>
                    )}
                    {props.onAlert && (
                        <button type="button" className="ps-icon-btn"
                            onClick={props.onAlert}
                            disabled={props.alertDisabled}
                            aria-label={t("picking_missing_btn")}
                            title={t("picking_missing_btn")}>
                            <AlertTriangle size={20} />
                        </button>
                    )}
                    {props.onBack && (
                        /* Same bordered treatment as the title-bar back
                           button (.page-back-btn) — the back affordance must
                           look identical whether the picker is on a list
                           page or mid-pick. */
                        <button type="button" className="ps-icon-btn"
                            onClick={props.onBack}
                            disabled={props.backDisabled}
                            aria-label="Back">
                            <ArrowLeft size={20} />
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default PickingTopBar;
